import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import axios from 'axios'
import { fileDownloadUrl } from '../../../redux/api'
import './ModernFileViewer.css'

interface FileItem {
  url: string
  name: string
  extension: string
}

interface ImageLoadState {
  [key: string]: 'loading' | 'loaded' | 'error'
}

interface LoadProgress {
  [key: string]: number // Percentage 0-100
}

/** Blob URLs from XHR (with credentials) so <img> does not re-request without auth */
interface ImageObjectUrls {
  [fileName: string]: string
}

export const ModernFileViewer = () => {
  const [searchParams] = useSearchParams()
  const intl = useIntl()

  // Parse files from URL (support multiple files)
  const [files, setFiles] = useState<FileItem[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
  const [imageLoadStates, setImageLoadStates] = useState<ImageLoadState>({})
  const [retryCount, setRetryCount] = useState<{[key: string]: number}>({})
  const [loadProgress, setLoadProgress] = useState<LoadProgress>({})
  const [imageRotation, setImageRotation] = useState<{[key: string]: number}>({}) // Rotation in degrees per image
  const [imageObjectUrls, setImageObjectUrls] = useState<ImageObjectUrls>({}) // Blob URLs from XHR (with credentials)
  const [image404Errors, setImage404Errors] = useState<{[fileName: string]: boolean}>({}) // Track 404 errors to prevent retries
  const image404Ref = useRef<Record<string, boolean>>({}) // Ref so handleImageError can skip retries synchronously

  useEffect(() => {
    // Parse files from URL parameters
    const rawFileUrl = searchParams.get('file')
    const fileName = searchParams.get('fileName')
    const fileId = searchParams.get('fileId') // Optional file ID for download
    const additionalFiles = searchParams.get('files') // JSON array of additional files

    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.maarefati.com'

    const constructUrl = (url: string) => {
      if (!url || typeof url !== 'string') return ''
      
      // Clean and trim the URL
      url = url.trim()
      
      // Reject obviously invalid URLs (just domains without paths)
      if (url === 'https://maarefati.com' || 
          url === 'http://maarefati.com' ||
          url === 'maarefati.com' ||
          url === 'https://api.maarefati.com' ||
          url === 'http://api.maarefati.com') {
        console.warn('Invalid file URL detected (domain only):', url)
        return ''
      }
      
      // If it's already a full URL, validate and return
      if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
          const urlObj = new URL(url)
          // Ensure it's not just a domain
          if (urlObj.pathname === '/' && !urlObj.search && !urlObj.hash) {
            console.warn('Invalid file URL (domain only):', url)
            return ''
          }
          return url
        } catch (e) {
          console.warn('Invalid URL format:', url)
          return ''
        }
      }
      
      // Handle relative URLs
      if (url.startsWith('/')) {
        return `${baseUrl}${url}`
      }
      
      // Otherwise, prepend base URL
      return `${baseUrl}/${url}`
    }

    const filesList: FileItem[] = []

    // Check if we have additional files (gallery mode)
    if (additionalFiles) {
      try {
        const parsed = JSON.parse(additionalFiles)
        if (Array.isArray(parsed)) {
          parsed.forEach((file: any) => {
            const extension = file.name.split('.').pop()?.toLowerCase() || ''
            filesList.push({
              url: constructUrl(file.url),
              name: file.name,
              extension,
            })
          })
          if (fileName) {
            const primaryIndex = filesList.findIndex(f => f.name === fileName)
            if (primaryIndex >= 0) {
              setCurrentIndex(primaryIndex)
            }
          }
        }
      } catch (e) {
        console.error('Error parsing additional files:', e)
        // Fallback: add primary file only
        if (rawFileUrl && fileName) {
          const extension = fileName.split('.').pop()?.toLowerCase() || ''
          filesList.push({
            url: constructUrl(rawFileUrl),
            name: fileName,
            extension,
          })
        }
      }
    } else {
      // Single file mode - add primary file only
      if (rawFileUrl && fileName) {
        const extension = fileName.split('.').pop()?.toLowerCase() || ''
        let constructedUrl = constructUrl(rawFileUrl)
        
        // If URL construction failed and we have a file ID, try to construct download URL
        if ((!constructedUrl || constructedUrl === '') && fileId) {
          constructedUrl = fileDownloadUrl(fileId)
        }
        
        filesList.push({
          url: constructedUrl || rawFileUrl, // Fallback to raw URL if construction fails
          name: fileName,
          extension,
        })
      }
    }

    setFiles(filesList)
  }, [searchParams])

  const currentFile = files[currentIndex]

  // Zoom controls
  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3))
  }, [])

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))
  }, [])

  const resetZoom = useCallback(() => {
    setZoomLevel(1)
    setImagePosition({ x: 0, y: 0 })
  }, [])

  // Rotation controls
  const rotateLeft = useCallback(() => {
    if (!currentFile) return
    setImageRotation((prev) => {
      const currentRotation = prev[currentFile.name] || 0
      const newRotation = (currentRotation - 90) % 360
      return { ...prev, [currentFile.name]: newRotation }
    })
  }, [currentFile])

  const rotateRight = useCallback(() => {
    if (!currentFile) return
    setImageRotation((prev) => {
      const currentRotation = prev[currentFile.name] || 0
      const newRotation = (currentRotation + 90) % 360
      return { ...prev, [currentFile.name]: newRotation }
    })
  }, [currentFile])

  const resetRotation = useCallback(() => {
    if (!currentFile) return
    setImageRotation((prev) => {
      const updated = { ...prev }
      delete updated[currentFile.name]
      return updated
    })
  }, [currentFile])

  // Navigation
  const nextImage = useCallback(() => {
    if (currentIndex < files.length - 1) {
      setCurrentIndex(currentIndex + 1)
      resetZoom()
    }
  }, [currentIndex, files.length, resetZoom])

  const prevImage = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
      resetZoom()
    }
  }, [currentIndex, resetZoom])

  const goToImage = useCallback(
    (index: number) => {
      setCurrentIndex(index)
      resetZoom()
    },
    [resetZoom]
  )

  // Download with robust error handling and fallback mechanisms
  const handleDownload = useCallback(async () => {
    if (!currentFile) {
      console.error('No current file available for download')
      alert(intl.formatMessage(
        { id: 'ERROR.NO_FILE', defaultMessage: 'No file available for download.' }
      ))
      return
    }

    // Get base URL for constructing full URLs - use relative URLs for same-origin requests
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.maarefati.com'
    const useRelativeUrl = !baseUrl.includes('api.maarefati.com') || window.location.hostname === 'localhost' || window.location.hostname.includes('maarefati.com')
    
    // Get file URL
    let fileUrl = currentFile.url || ''
    
    // If URL is still empty or invalid, try to get from search params
    if (!fileUrl || fileUrl.trim() === '') {
      const searchParams = new URLSearchParams(window.location.search)
      const rawFileUrl = searchParams.get('file')
      const fileId = searchParams.get('fileId')
      
      // If we have a file ID, construct download URL using the API function
      if (fileId) {
        fileUrl = fileDownloadUrl(fileId)
      } else if (rawFileUrl) {
        fileUrl = rawFileUrl
      }
    }
    
    // If URL is still invalid (just a domain), try to extract file ID from URL if possible
    if ((!fileUrl || fileUrl === 'https://maarefati.com' || fileUrl === 'http://maarefati.com') && currentFile.name) {
      // Try to see if we can get file ID from search params
      const searchParams = new URLSearchParams(window.location.search)
      const fileId = searchParams.get('fileId')
      if (fileId) {
        fileUrl = fileDownloadUrl(fileId)
      }
    }
    
    // Validate and construct full URL
    fileUrl = fileUrl.trim()
    
    // Check if URL is just a domain - try to use fileId if available
    const isJustDomain = fileUrl === 'https://maarefati.com' || 
                         fileUrl === 'http://maarefati.com' ||
                         fileUrl === 'maarefati.com' ||
                         fileUrl === 'https://api.maarefati.com' ||
                         fileUrl === 'http://api.maarefati.com' ||
                         (fileUrl.startsWith('https://maarefati.com') && fileUrl.split('/').length <= 3) ||
                         (fileUrl.startsWith('http://maarefati.com') && fileUrl.split('/').length <= 3)
    
    if (isJustDomain) {
      // Try one more time to get fileId from search params
      const searchParams = new URLSearchParams(window.location.search)
      const fileId = searchParams.get('fileId')
      
      if (fileId) {
        fileUrl = fileDownloadUrl(fileId)
      } else {
        console.error('Invalid file URL (domain only) and no fileId available:', fileUrl)
        alert('Invalid file URL. The URL provided is just a domain name, not a file download link.\n\nPlease ensure the file URL includes the full path to the file (e.g., /api/files/{fileId}/download).')
        return
      }
    }
    
    if (!fileUrl) {
      console.error('No file URL available', { 
        currentFile, 
        currentFileUrl: currentFile.url,
        searchParams: window.location.search 
      })
      alert('Invalid file URL. Cannot download file.')
      return
    }

    // Check if URL contains /api/files/ but doesn't end with /download - convert to download URL
    if (fileUrl.includes('/api/files/') && !fileUrl.includes('/download')) {
      const fileIdMatch = fileUrl.match(/\/api\/files\/([^\/]+)/)
      if (fileIdMatch && fileIdMatch[1]) {
        const fileId = fileIdMatch[1]
        // Use relative URL - axios will handle base URL
        fileUrl = `/api/files/${fileId}/download`
      }
    }

    // Keep relative URLs as-is for axios (it will use the base URL from setup)
    // Only construct full URL if it's not already a full URL and not an API endpoint
    const originalFileUrl = fileUrl
    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
      // For API endpoints, keep as relative - axios will handle it
      if (fileUrl.startsWith('/api/')) {
        // Keep relative - axios will use base URL
      } else if (fileUrl.startsWith('/')) {
        fileUrl = `${baseUrl}${fileUrl}`
      } else {
        fileUrl = `${baseUrl}/${fileUrl}`
      }
    }

    // Validate URL format - be lenient
    try {
      const urlObj = new URL(fileUrl)
      // URL is valid
    } catch (e) {
      console.warn('URL validation failed, but proceeding with download:', fileUrl, e)
      // Don't block - let the download attempt proceed
    }

    // Strategy 1: Try direct download (works for same-origin and public files)
    const tryDirectDownload = () => {
      return new Promise<void>((resolve, reject) => {
        const link = document.createElement('a')
        link.href = fileUrl
        link.download = currentFile.name
        link.setAttribute('target', '_self')
        link.style.display = 'none'
        
        // Add event listeners to detect if download actually started
        const cleanup = () => {
          if (link.parentNode) {
            link.parentNode.removeChild(link)
          }
          clearTimeout(timeout)
        }
        
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('Download timeout'))
        }, 2000)
        
        link.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          setTimeout(() => {
            cleanup()
            resolve()
          }, 100)
        }
        
        document.body.appendChild(link)
        // Use setTimeout to ensure the link is in the DOM before clicking
        setTimeout(() => {
          link.click()
          // Clean up after a short delay
          setTimeout(() => {
            cleanup()
            resolve()
          }, 100)
        }, 0)
      })
    }

    // Strategy 2: Axios with blob (works for cross-origin and authenticated files)
    const tryFetchDownload = async () => {
      try {
        // Check if URL is from our API (contains /api/) - use axios with auth
        const isApiUrl = fileUrl.includes('/api/')
        
        // Use the URL directly - fileDownloadUrl already returns full URL
        // For API endpoints, axios will include auth headers via interceptors
        const downloadUrl = fileUrl
        
        const response = await axios.get(downloadUrl, {
          responseType: 'blob',
          timeout: 60000, // 60 second timeout for large files
          withCredentials: true,
        })

        // Check if response is actually a blob
        if (!(response.data instanceof Blob)) {
          throw new Error('Response is not a blob')
        }

        // Preserve the original blob type from response if available
        const blobType = response.data.type || response.headers['content-type'] || ''
        const blob = blobType 
          ? new Blob([response.data], { type: blobType })
          : new Blob([response.data])
        
        const blobUrl = URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = currentFile.name
        link.style.display = 'none'
        link.setAttribute('target', '_self')
        document.body.appendChild(link)
        // Use setTimeout to ensure the link is in the DOM before clicking
        setTimeout(() => {
          link.click()
          // Clean up after a short delay
          setTimeout(() => {
            if (link.parentNode) {
              link.parentNode.removeChild(link)
            }
            URL.revokeObjectURL(blobUrl)
          }, 100)
        }, 0)
      } catch (error) {
        console.error('Download error:', error)
        throw error
      }
    }

    // Try both strategies with proper error handling
    try {
      // First try fetch method (more reliable for downloads)
      await tryFetchDownload()
    } catch (error: any) {
      console.error('Fetch download failed:', error)
      
      try {
        // Fallback to direct download
        await tryDirectDownload()
      } catch (directError: any) {
        console.error('Both download methods failed:', directError)
        
        // Show detailed error message
        const errorMessage = error?.response?.status 
          ? `Download failed: HTTP ${error.response.status}`
          : error?.code === 'ERR_NETWORK'
          ? 'Network error: Cannot connect to server. The file URL may be invalid.'
          : error?.message || 'Download failed'
        
        console.error('Final error:', errorMessage)
        
        // Check if URL is just a domain - provide helpful message
        if (fileUrl === 'https://maarefati.com' || fileUrl === 'http://maarefati.com' || fileUrl.includes('maarefati.com') && !fileUrl.includes('/api/')) {
          alert('Invalid file URL. The URL provided is just a domain name, not a file download link.\n\nPlease check that the file URL is correct and includes the full path to the file.')
          return
        }
        
        // Final fallback: open in new tab
        const userConfirmed = window.confirm(
          'Download failed. Would you like to open the file in a new tab instead?'
        )
        if (userConfirmed) {
          window.open(fileUrl, '_blank', 'noopener,noreferrer')
        } else {
          alert(`Failed to download file. Please check your connection and try again.\n\nError: ${errorMessage}`)
        }
      }
    }
  }, [currentFile, intl])

  // Download all files with proper error handling
  const handleDownloadAll = useCallback(async () => {
    if (files.length === 0) return

    const downloadFile = async (file: FileItem, index: number): Promise<void> => {
      return new Promise((resolve) => {
        setTimeout(async () => {
          if (!file.url) {
            console.error(`No URL for file: ${file.name}`)
            resolve()
            return
          }

          // Validate URL
          let fileUrl = file.url.trim()
          if (!fileUrl || fileUrl === 'https://maarefati.com' || fileUrl === 'http://maarefati.com') {
            console.error(`Invalid URL for file: ${file.name}`)
            resolve()
            return
          }

          try {
            // Use axios for authenticated downloads (exercice/remarque attachments)
            const response = await axios.get(fileUrl, {
              responseType: 'blob',
              timeout: 60000, // 60 second timeout for large files
              withCredentials: true,
            })

            // Preserve the original blob type from response if available
            const blobType = response.data.type || response.headers['content-type'] || ''
            const blob = blobType 
              ? new Blob([response.data], { type: blobType })
              : new Blob([response.data])
            
            const blobUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = file.name
            link.style.display = 'none'
            link.setAttribute('target', '_self')
            document.body.appendChild(link)
            setTimeout(() => {
              link.click()
              // Safely remove the link if it's still in the DOM
              setTimeout(() => {
                if (link.parentNode) {
                  link.parentNode.removeChild(link)
                }
                URL.revokeObjectURL(blobUrl)
              }, 100)
            }, 0)
          } catch (error) {
            console.error(`Error downloading ${file.name}:`, error)
            // Fallback to direct link
            const link = document.createElement('a')
            link.href = fileUrl
            link.download = file.name
            link.setAttribute('target', '_self')
            link.style.display = 'none'
            document.body.appendChild(link)
            setTimeout(() => {
              link.click()
              setTimeout(() => {
                if (link.parentNode) {
                  link.parentNode.removeChild(link)
                }
              }, 100)
            }, 0)
          }
          resolve()
        }, index * 300) // Stagger downloads by 300ms
      })
    }

    // Download all files sequentially to avoid overwhelming the browser
    for (let i = 0; i < files.length; i++) {
      await downloadFile(files[i], i)
    }
  }, [files])

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen)
  }, [isFullscreen])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          prevImage()
          break
        case 'ArrowRight':
          nextImage()
          break
        case '+':
        case '=':
          zoomIn()
          break
        case '-':
        case '_':
          zoomOut()
          break
        case '0':
          resetZoom()
          break
        case 'Escape':
          if (isFullscreen) setIsFullscreen(false)
          break
        case 'f':
        case 'F':
          toggleFullscreen()
          break
        case 'r':
        case 'R':
          if (isImage(currentFile?.extension)) rotateRight()
          break
        case 'l':
        case 'L':
          if (isImage(currentFile?.extension)) rotateLeft()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextImage, prevImage, zoomIn, zoomOut, resetZoom, isFullscreen, toggleFullscreen, rotateLeft, rotateRight, currentFile])

  // Drag to pan when zoomed
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const isImage = (extension: string) => {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)
  }

  // Load image with progress tracking using XMLHttpRequest
  const loadImageWithProgress = (url: string, fileName: string) => {
    if (image404Ref.current[fileName]) {
      setImageLoadStates(prev => ({ ...prev, [fileName]: 'error' }))
      setLoadProgress(prev => ({ ...prev, [fileName]: 0 }))
      return
    }
    setImageLoadStates(prev => ({...prev, [fileName]: 'loading'}))
    setLoadProgress(prev => ({...prev, [fileName]: 0}))

    const xhr = new XMLHttpRequest()
    
    // Track download progress
    xhr.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100)
        setLoadProgress(prev => ({...prev, [fileName]: percentage}))
      } else {
        // If server doesn't send content-length, simulate progress
        const elapsed = Date.now() - startTime
        const simulatedProgress = Math.min(90, Math.floor(elapsed / 100)) // Max 90% simulated
        setLoadProgress(prev => ({...prev, [fileName]: simulatedProgress}))
      }
    })

    const startTime = Date.now()

    // On successful load: store blob URL so main/thumb <img> use it (no second unauthenticated request)
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        setLoadProgress(prev => ({...prev, [fileName]: 100}))
        
        const blob = xhr.response
        const objectUrl = URL.createObjectURL(blob)
        setImageObjectUrls(prev => {
          const prevUrl = prev[fileName]
          if (prevUrl) URL.revokeObjectURL(prevUrl)
          return { ...prev, [fileName]: objectUrl }
        })
        
        const img = new Image()
        img.onload = () => {
          setImageLoadStates(prev => ({...prev, [fileName]: 'loaded'}))
        }
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          setImageObjectUrls(prev => {
            const next = { ...prev }
            delete next[fileName]
            return next
          })
          handleImageError(fileName, url)
        }
        img.src = objectUrl
      } else if (xhr.status === 404) {
        // 404 = file missing or route not serving static files (per testimage.md); do not retry
        image404Ref.current[fileName] = true
        setImage404Errors(prev => ({ ...prev, [fileName]: true }))
        setImageLoadStates(prev => ({ ...prev, [fileName]: 'error' }))
        setLoadProgress(prev => ({ ...prev, [fileName]: 0 }))
      } else {
        handleImageError(fileName, url)
      }
    })

    // On error
    xhr.addEventListener('error', () => {
      handleImageError(fileName, url)
    })

    // On timeout
    xhr.addEventListener('timeout', () => {
      handleImageError(fileName, url)
    })

    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    xhr.timeout = 30000 // 30 second timeout
    // Send cookies/auth so API can authorize exercice/remarque attachment requests
    xhr.withCredentials = true
    xhr.send()
  }

  // Handle image load
  const handleImageLoad = (fileName: string) => {
    setImageLoadStates(prev => ({...prev, [fileName]: 'loaded'}))
    setLoadProgress(prev => ({...prev, [fileName]: 100}))
  }

  // Strip existing retry/t params so we don't duplicate them when retrying
  const stripRetryParams = (url: string): string => {
    try {
      const u = new URL(url)
      u.searchParams.delete('retry')
      u.searchParams.delete('t')
      return u.toString()
    } catch {
      return url.replace(/\?retry=\d+&t=\d+/g, '').replace(/&retry=\d+&t=\d+/g, '')
    }
  }

  // Append retry/cache-bust params (use base URL without previous retry params)
  const appendRetryParams = (baseUrl: string, retry: number) => {
    const clean = stripRetryParams(baseUrl)
    const sep = clean.includes('?') ? '&' : '?'
    return `${clean}${sep}retry=${retry}&t=${Date.now()}`
  }

  // Handle image error with retry (skip retries if this image is known to be 404)
  const handleImageError = (fileName: string, url: string) => {
    if (image404Ref.current[fileName]) {
      setImageLoadStates(prev => ({ ...prev, [fileName]: 'error' }))
      setLoadProgress(prev => ({ ...prev, [fileName]: 0 }))
      return
    }
    const currentRetries = retryCount[fileName] || 0

    if (currentRetries < 3) {
      setRetryCount(prev => ({...prev, [fileName]: currentRetries + 1}))
      setTimeout(() => {
        loadImageWithProgress(appendRetryParams(url, currentRetries + 1), fileName)
      }, 1000 * (currentRetries + 1))
    } else {
      setImageLoadStates(prev => ({...prev, [fileName]: 'error'}))
      setLoadProgress(prev => ({...prev, [fileName]: 0}))
    }
  }

  // Preload current image with progress
  useEffect(() => {
    if (currentFile && isImage(currentFile.extension) && !imageLoadStates[currentFile.name]) {
      loadImageWithProgress(currentFile.url, currentFile.name)
    }
  }, [currentFile])

  // Set initial loading states and preload thumbnails
  useEffect(() => {
    const initialStates: ImageLoadState = {}
    files.forEach((file, index) => {
      if (isImage(file.extension)) {
        initialStates[file.name] = 'loading'
        
        // Preload thumbnails progressively (not all at once)
        setTimeout(() => {
          loadImageWithProgress(file.url, file.name)
        }, index * 200) // Stagger by 200ms each
      }
    })
    setImageLoadStates(initialStates)
  }, [files])

  const imageObjectUrlsRef = useRef(imageObjectUrls)
  imageObjectUrlsRef.current = imageObjectUrls
  useEffect(() => {
    return () => {
      Object.values(imageObjectUrlsRef.current).forEach((url) => {
        try { URL.revokeObjectURL(url) } catch (_) { /* ignore */ }
      })
    }
  }, [])

  if (files.length === 0 || !currentFile) {
    return (
      <div className='modern-file-viewer no-files'>
        <div className='no-files-message'>
          <i className='bi bi-file-earmark-x' style={{ fontSize: '4rem', color: '#64748b' }}></i>
          <h3>
            <FormattedMessage id='ERROR.NO_FILES' defaultMessage='No files available' />
          </h3>
          <p>
            <FormattedMessage
              id='ERROR.NO_FILES_DESC'
              defaultMessage='No files were provided to display.'
            />
          </p>
          <button className='btn btn-primary' onClick={() => window.close()}>
            <FormattedMessage id='MENU.CLOSE' defaultMessage='Close' />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`modern-file-viewer ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Header */}
      <div className='viewer-header'>
        <div className='header-left'>
          <img
            src={toAbsoluteUrl('/media/logos/maarefati-logo.png')}
            alt='Maarefati'
            className='logo'
          />
          <div className='file-info'>
            <h2 className='file-name'>{currentFile.name}</h2>
            <span className='file-counter'>
              {currentIndex + 1} / {files.length}
            </span>
          </div>
        </div>

        <div className='header-right'>
          {/* Unified Toolbar Container */}
          <div className='toolbar-container'>
            {/* Zoom Controls Group */}
            <div className='toolbar-group'>
              <button
                className='btn-icon'
                onClick={zoomOut}
                disabled={zoomLevel <= 0.5}
                title={intl.formatMessage({ id: 'MENU.ZOOM_OUT', defaultMessage: 'Zoom Out' })}
              >
                <i className='bi bi-dash-circle'></i>
              </button>
              <span className='zoom-level'>{Math.round(zoomLevel * 100)}%</span>
              <button
                className='btn-icon'
                onClick={zoomIn}
                disabled={zoomLevel >= 3}
                title={intl.formatMessage({ id: 'MENU.ZOOM_IN', defaultMessage: 'Zoom In' })}
              >
                <i className='bi bi-plus-circle'></i>
              </button>
              <button
                className='btn-icon'
                onClick={resetZoom}
                title={intl.formatMessage({ id: 'MENU.RESET_ZOOM', defaultMessage: 'Reset Zoom' })}
              >
                <i className='bi bi-arrows-angle-contract'></i>
              </button>
            </div>

            {/* Rotation Controls Group - Only for images */}
            {currentFile && isImage(currentFile.extension) && (
              <div className='toolbar-group toolbar-divider rotation-toolbar-group'>
                <button
                  className='btn-icon'
                  onClick={rotateLeft}
                  title={intl.formatMessage({ id: 'MENU.ROTATE_LEFT', defaultMessage: 'Rotate Left' })}
                >
                  <i className='bi bi-arrow-counterclockwise'></i>
                </button>
                <button
                  className='btn-icon'
                  onClick={rotateRight}
                  title={intl.formatMessage({ id: 'MENU.ROTATE_RIGHT', defaultMessage: 'Rotate Right' })}
                >
                  <i className='bi bi-arrow-clockwise'></i>
                </button>
                {(imageRotation[currentFile.name] || 0) !== 0 && (
                  <button
                    className='btn-icon'
                    onClick={resetRotation}
                    title={intl.formatMessage({ id: 'MENU.RESET_ROTATION', defaultMessage: 'Reset Rotation' })}
                  >
                    <i className='bi bi-arrow-repeat'></i>
                  </button>
                )}
              </div>
            )}

            {/* Action Buttons Group */}
            <div className='toolbar-group toolbar-divider'>
              <button
                className='btn-icon'
                onClick={toggleFullscreen}
                title={intl.formatMessage({
                  id: 'MENU.FULLSCREEN',
                  defaultMessage: 'Fullscreen',
                })}
              >
                <i className={`bi bi-${isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}`}></i>
              </button>

              <button
                className='btn-icon'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleDownload()
                }}
                title={intl.formatMessage({ id: 'MENU.DOWNLOAD', defaultMessage: 'Download' })}
              >
                <i className='bi bi-download'></i>
              </button>

              {files.length > 1 && (
                <button
                  className='btn-icon'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleDownloadAll()
                  }}
                  title={intl.formatMessage({
                    id: 'MENU.DOWNLOAD_ALL',
                    defaultMessage: 'Download All',
                  })}
                >
                  <i className='bi bi-cloud-arrow-down'></i>
                </button>
              )}

              <button
                className='btn-icon btn-close-viewer'
                onClick={() => window.close()}
                title={intl.formatMessage({ id: 'MENU.CLOSE', defaultMessage: 'Close' })}
              >
                <i className='bi bi-x-lg'></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='viewer-content'>
        {/* Thumbnail Sidebar */}
        {files.length > 1 && (
          <div className='thumbnail-sidebar'>
            {/* Scroll Up Button */}
            {currentIndex > 0 && (
              <button className='scroll-btn scroll-up' onClick={() => goToImage(currentIndex - 1)}>
                <i className='bi bi-chevron-up'></i>
              </button>
            )}

            {/* Thumbnails */}
            <div className='thumbnails-container'>
              {files.map((file, index) => (
                <div
                  key={index}
                  className={`thumbnail ${index === currentIndex ? 'active' : ''} ${imageLoadStates[file.name] === 'loading' ? 'loading' : ''}`}
                  onClick={() => goToImage(index)}
                >
                  {isImage(file.extension) ? (
                    <>
                      <img 
                        src={imageObjectUrls[file.name] || file.url} 
                        alt={file.name}
                        loading="lazy"
                        onLoad={() => handleImageLoad(file.name)}
                        onError={() => {
                          setImageLoadStates(prev => ({...prev, [file.name]: 'error'}))
                        }}
                        style={{
                          opacity: imageLoadStates[file.name] === 'loaded' ? 1 : 0.3
                        }}
                      />
                      {imageLoadStates[file.name] === 'loading' && loadProgress[file.name] !== undefined && (
                        <div className='thumbnail-loading-overlay'>
                          <div className='mini-circular-progress'>
                            <svg width='40' height='40' viewBox='0 0 40 40'>
                              <circle
                                cx='20'
                                cy='20'
                                r='16'
                                fill='none'
                                stroke='rgba(255,255,255,0.3)'
                                strokeWidth='3'
                              />
                              <circle
                                cx='20'
                                cy='20'
                                r='16'
                                fill='none'
                                stroke='white'
                                strokeWidth='3'
                                strokeLinecap='round'
                                strokeDasharray={`${2 * Math.PI * 16}`}
                                strokeDashoffset={`${2 * Math.PI * 16 * (1 - (loadProgress[file.name] || 0) / 100)}`}
                                transform='rotate(-90 20 20)'
                              />
                              <text
                                x='20'
                                y='20'
                                textAnchor='middle'
                                dominantBaseline='middle'
                                style={{
                                  fontSize: '8px',
                                  fontWeight: 'bold',
                                  fill: 'white'
                                }}
                              >
                                {loadProgress[file.name] || 0}%
                              </text>
                            </svg>
                          </div>
                        </div>
                      )}
                      {imageLoadStates[file.name] === 'error' && (
                        <div className='thumbnail-placeholder' style={{background: '#fee2e2'}}>
                          <i className='bi bi-exclamation-triangle' style={{color: '#ef4444'}}></i>
                          <span style={{fontSize: '0.5rem', color: '#ef4444'}}>Error</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className='thumbnail-placeholder'>
                      <i className='bi bi-file-earmark'></i>
                      <span>{file.extension.toUpperCase()}</span>
                    </div>
                  )}
                  {index === currentIndex && <div className='thumbnail-indicator'></div>}
                </div>
              ))}
            </div>

            {/* Scroll Down Button */}
            {currentIndex < files.length - 1 && (
              <button className='scroll-btn scroll-down' onClick={() => goToImage(currentIndex + 1)}>
                <i className='bi bi-chevron-down'></i>
              </button>
            )}
          </div>
        )}

        {/* Main Display Area */}
        <div className='main-display'>
          {/* Navigation Arrows */}
          {files.length > 1 && (
            <>
              <button
                className='nav-arrow nav-prev'
                onClick={prevImage}
                disabled={currentIndex === 0}
              >
                <i className='bi bi-chevron-left'></i>
              </button>
              <button
                className='nav-arrow nav-next'
                onClick={nextImage}
                disabled={currentIndex === files.length - 1}
              >
                <i className='bi bi-chevron-right'></i>
              </button>
            </>
          )}

          {/* Image Display */}
          <div
            className='image-display'
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            {isImage(currentFile.extension) ? (
              <>
                {/* Loading Spinner with Percentage */}
                {imageLoadStates[currentFile.name] === 'loading' && (
                  <div className='image-loading-overlay'>
                    {/* Circular Progress */}
                    <div className='circular-progress'>
                      <svg width='120' height='120' viewBox='0 0 120 120'>
                        {/* Background circle */}
                        <circle
                          cx='60'
                          cy='60'
                          r='54'
                          fill='none'
                          stroke='#e2e8f0'
                          strokeWidth='8'
                        />
                        {/* Progress circle */}
                        <circle
                          cx='60'
                          cy='60'
                          r='54'
                          fill='none'
                          stroke='#0ea5e9'
                          strokeWidth='8'
                          strokeLinecap='round'
                          strokeDasharray={`${2 * Math.PI * 54}`}
                          strokeDashoffset={`${2 * Math.PI * 54 * (1 - (loadProgress[currentFile.name] || 0) / 100)}`}
                          transform='rotate(-90 60 60)'
                          style={{transition: 'stroke-dashoffset 0.3s ease'}}
                        />
                        {/* Percentage text */}
                        <text
                          x='60'
                          y='60'
                          textAnchor='middle'
                          dominantBaseline='middle'
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            fill: '#1e293b'
                          }}
                        >
                          {loadProgress[currentFile.name] || 0}%
                        </text>
                      </svg>
                    </div>
                    <p className='mt-3 text-muted fw-bold'>
                      <FormattedMessage id='STATUS.LOADING_IMAGE' defaultMessage='Loading image...' />
                    </p>
                    <div className='progress mt-2' style={{width: '200px', height: '8px'}}>
                      <div 
                        className='progress-bar progress-bar-striped progress-bar-animated bg-primary' 
                        role='progressbar'
                        style={{width: `${loadProgress[currentFile.name] || 0}%`}}
                        aria-valuenow={loadProgress[currentFile.name] || 0}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {imageLoadStates[currentFile.name] === 'error' && (
                  <div className='file-preview-unsupported'>
                    <i className='bi bi-image' style={{ fontSize: '4rem', color: '#ef4444' }}></i>
                    <h3 style={{color: '#ef4444'}}>
                      {image404Errors[currentFile.name] ? (
                        <FormattedMessage id='ERROR.IMAGE_NOT_FOUND' defaultMessage='Image Not Found' />
                      ) : (
                        <FormattedMessage id='ERROR.IMAGE_FAILED' defaultMessage='Failed to Load Image' />
                      )}
                    </h3>
                    <p>
                      {image404Errors[currentFile.name] ? (
                        <FormattedMessage
                          id='ERROR.IMAGE_NOT_FOUND_DESC'
                          defaultMessage='The image file is missing or not available at the server.'
                        />
                      ) : (
                        <FormattedMessage
                          id='ERROR.IMAGE_FAILED_DESC'
                          defaultMessage='The image could not be loaded. It may be corrupted or the connection is slow.'
                        />
                      )}
                    </p>
                    <div className='d-flex gap-2 justify-content-center'>
                      {!image404Errors[currentFile.name] && (
                        <button 
                          className='btn btn-primary' 
                          onClick={() => {
                            setRetryCount(prev => ({...prev, [currentFile.name]: 0}))
                            setImageLoadStates(prev => ({...prev, [currentFile.name]: 'loading'}))
                            setTimeout(() => window.location.reload(), 100)
                          }}
                        >
                          <i className='bi bi-arrow-clockwise me-2'></i>
                          <FormattedMessage id='MENU.RETRY' defaultMessage='Retry' />
                        </button>
                      )}
                      <button 
                        className='btn btn-secondary' 
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleDownload()
                        }}
                      >
                        <i className='bi bi-download me-2'></i>
                        <FormattedMessage id='MENU.DOWNLOAD' defaultMessage='Download' />
                      </button>
                    </div>
                  </div>
                )}

                {/* Actual Image: use blob URL from XHR (with credentials) when available */}
                <img
                  src={imageObjectUrls[currentFile.name] || (() => {
                    const u = currentFile.url
                    const r = retryCount[currentFile.name]
                    if (!r) return u
                    const sep = u.includes('?') ? '&' : '?'
                    return `${u}${sep}retry=${r}`
                  })()}
                  alt={currentFile.name}
                  style={{
                    transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${
                      imagePosition.y / zoomLevel
                    }px) rotate(${imageRotation[currentFile.name] || 0}deg)`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease',
                    display: imageLoadStates[currentFile.name] === 'loaded' ? 'block' : 'none',
                    transformOrigin: 'center center',
                  }}
                  draggable={false}
                  onLoad={() => handleImageLoad(currentFile.name)}
                  onError={() => handleImageError(currentFile.name, currentFile.url)}
                />
              </>
            ) : (
              <div className='file-preview-unsupported'>
                <i className='bi bi-file-earmark-text' style={{ fontSize: '4rem' }}></i>
                <h3>
                  {currentFile.extension.toUpperCase()}{' '}
                  <FormattedMessage id='ERROR.FILE_PREVIEW' defaultMessage='Preview Not Available' />
                </h3>
                <p>
                  <FormattedMessage
                    id='ERROR.DOWNLOAD_TO_VIEW'
                    defaultMessage='Download the file to view it.'
                  />
                </p>
                <button 
                  className='btn btn-primary' 
                  type='button'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Download button clicked for non-image file')
                    handleDownload()
                  }}
                >
                  <i className='bi bi-download me-2'></i>
                  <FormattedMessage id='MENU.DOWNLOAD' defaultMessage='Download' />
                </button>
              </div>
            )}
          </div>

          {/* Image Info Overlay */}
          <div className='image-info-overlay'>
            <span className='image-name'>{currentFile.name}</span>
            <span className='image-size'>
              {files.length > 1 && `${currentIndex + 1}/${files.length}`}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom Rotation Controls (Mobile-Friendly) */}
      {currentFile && isImage(currentFile.extension) && (
        <div className='bottom-rotation-controls'>
          <button
            className='btn-rotate btn-rotate-left'
            onClick={rotateLeft}
            title={intl.formatMessage({ id: 'MENU.ROTATE_LEFT', defaultMessage: 'Rotate Left' })}
          >
            <i className='bi bi-arrow-counterclockwise'></i>
            <span className='btn-label'>
              <FormattedMessage id='MENU.ROTATE_LEFT' defaultMessage='Rotate Left' />
            </span>
          </button>
          {(imageRotation[currentFile.name] || 0) !== 0 && (
            <button
              className='btn-rotate btn-rotate-reset'
              onClick={resetRotation}
              title={intl.formatMessage({ id: 'MENU.RESET_ROTATION', defaultMessage: 'Reset Rotation' })}
            >
              <i className='bi bi-arrow-repeat'></i>
              <span className='btn-label'>
                <FormattedMessage id='MENU.RESET_ROTATION' defaultMessage='Reset' />
              </span>
            </button>
          )}
          <button
            className='btn-rotate btn-rotate-right'
            onClick={rotateRight}
            title={intl.formatMessage({ id: 'MENU.ROTATE_RIGHT', defaultMessage: 'Rotate Right' })}
          >
            <i className='bi bi-arrow-clockwise'></i>
            <span className='btn-label'>
              <FormattedMessage id='MENU.ROTATE_RIGHT' defaultMessage='Rotate Right' />
            </span>
          </button>
        </div>
      )}

      {/* Keyboard Shortcuts Help */}
      <div className='keyboard-shortcuts-hint'>
        <i className='bi bi-keyboard me-2'></i>
        <span>
          <FormattedMessage
            id='MENU.KEYBOARD_SHORTCUTS'
            defaultMessage='← → Navigate | + - Zoom | R/L Rotate | F Fullscreen | ESC Exit'
          />
        </span>
      </div>
    </div>
  )
}

